import React from 'react';

import './ProcessFigures.css';

/*
 * Nine animated mini-infographics ("plates"), one per process stage.
 * Pure CSS keyframes — no JavaScript. Every plate's base style is the
 * finished scene, so prerendered, no-JS and reduced-motion visitors
 * see a completed still instead of an empty frame. Plates are
 * decorative (aria-hidden); the stage copy carries the meaning.
 */

const dv = (i: number): React.CSSProperties => ({ '--d': i } as React.CSSProperties);

const PROMPT_PILLS = ['📋 Requirements', '🎨 Design', '📊 Tasks'];

export const PromptPlate: React.FC = () => (
  <div className="psf psf-prompt" aria-hidden="true">
    <div className="psf-prompt__bar">
      <span className="psf-prompt__text">A CRM for gyms - bookings, payments, member app</span>
      <span className="psf-prompt__caret" />
      <span className="psf-prompt__send"><i className="fa-solid fa-arrow-up" /></span>
    </div>
    <div className="psf-prompt__pills">
      {PROMPT_PILLS.map((p, i) => (
        <span key={p} className="psf-prompt__pill" style={dv(i)}>{p}</span>
      ))}
    </div>
  </div>
);

const DOC_LINES = [92, 74, 86, 58];

export const DocPlate: React.FC = () => (
  <div className="psf psf-doc" aria-hidden="true">
    <div className="psf-doc__sheet">
      <span className="psf-doc__head" />
      {DOC_LINES.map((w, i) => (
        <span
          key={w}
          className="psf-doc__ln"
          style={{ '--w': `${w}%`, '--d': i } as React.CSSProperties}
        />
      ))}
      <span className="psf-doc__diff psf-doc__diff--add">+ Member referral rewards</span>
      <span className="psf-doc__diff psf-doc__diff--del">− Manual invoicing</span>
    </div>
    <div className="psf-doc__actions">
      <span className="psf-doc__chip psf-doc__chip--ok"><i className="fa-solid fa-check" /> Accept</span>
      <span className="psf-doc__chip"><i className="fa-solid fa-xmark" /> Reject</span>
    </div>
  </div>
);

const FLOW_EDGES = [
  'M70 83 C78 83 74 35 82 35',
  'M70 83 C78 83 74 131 82 131',
  'M140 35 H158',
  'M216 35 H228',
  'M257 48 V118',
];

const FLOW_NODES = [
  { x: 12, y: 70, label: 'Home' },
  { x: 82, y: 22, label: 'Classes' },
  { x: 82, y: 118, label: 'Profile' },
  { x: 158, y: 22, label: 'Book' },
  { x: 228, y: 22, label: 'Pay' },
  { x: 228, y: 118, label: '✓ Booked' },
];

export const FlowPlate: React.FC = () => (
  <div className="psf psf-flow" aria-hidden="true">
    <div className="psf-flow__personas">
      <span className="psf-flow__persona psf-flow__persona--a">Member</span>
      <span className="psf-flow__persona psf-flow__persona--b">Trainer</span>
    </div>
    <svg viewBox="0 0 300 156" className="psf-flow__svg">
      {FLOW_EDGES.map((d, i) => (
        <path key={d} d={d} pathLength={1} className="psf-flow__edge" style={dv(i)} />
      ))}
      {FLOW_NODES.map((n, i) => (
        <g key={n.label} className="psf-flow__node" style={dv(i)}>
          <rect x={n.x} y={n.y} width="58" height="26" rx="8" />
          <text x={n.x + 29} y={n.y + 17}>{n.label}</text>
        </g>
      ))}
    </svg>
  </div>
);

export const DesignPlate: React.FC = () => (
  <div className="psf psf-design" aria-hidden="true">
    <div className="psf-browser">
      <div className="psf-browser__bar">
        <span className="psf-browser__dots" />
        <span className="psf-browser__url" />
      </div>
      <div className="psf-browser__view">
        <span className="psf-wire psf-wire--nav" style={dv(0)} />
        <span className="psf-wire psf-wire--hero" style={dv(1)} />
        <div className="psf-wire__row">
          <span className="psf-wire psf-wire--card" style={dv(2)} />
          <span className="psf-wire psf-wire--card" style={dv(3)} />
          <span className="psf-wire psf-wire--card" style={dv(4)} />
        </div>
        <span className="psf-wire psf-wire--cta" style={dv(5)} />
      </div>
    </div>
    <div className="psf-design__devices">
      <i className="fa-solid fa-desktop psf-design__dev" style={dv(0)} />
      <i className="fa-solid fa-tablet-screen-button psf-design__dev" style={dv(1)} />
      <i className="fa-solid fa-mobile-screen psf-design__dev" style={dv(2)} />
    </div>
  </div>
);

const INSPECTOR_ROWS = [
  { label: 'Font', value: 'Inter · 600' },
  { label: 'Size', value: '32 px' },
  { label: 'Color', value: '#60a5fa', swatch: true },
  { label: 'Radius', value: '12 px' },
];

export const EditPlate: React.FC = () => (
  <div className="psf psf-edit" aria-hidden="true">
    <div className="psf-browser psf-edit__canvas">
      <div className="psf-browser__bar">
        <span className="psf-browser__dots" />
        <span className="psf-browser__url" />
      </div>
      <div className="psf-browser__view">
        <span className="psf-wire psf-wire--nav" style={dv(0)} />
        <span className="psf-edit__target" style={dv(1)}>
          <span className="psf-edit__label">Hero heading</span>
          <span className="psf-edit__cursor"><i className="fa-solid fa-arrow-pointer" /></span>
        </span>
        <div className="psf-wire__row">
          <span className="psf-wire psf-wire--card" style={dv(2)} />
          <span className="psf-wire psf-wire--card" style={dv(3)} />
        </div>
      </div>
    </div>
    <div className="psf-edit__inspector">
      <span className="psf-edit__title">Inspector</span>
      {INSPECTOR_ROWS.map((row, i) => (
        <span key={row.label} className="psf-edit__row" style={dv(i)}>
          <span className="psf-edit__key">{row.label}</span>
          <span className="psf-edit__val">
            {row.swatch && <span className="psf-edit__swatch" />}
            {row.value}
          </span>
        </span>
      ))}
      <span className="psf-edit__save"><i className="fa-solid fa-check" /> Saved · 0 credits</span>
    </div>
  </div>
);

const SCAN_CHECKS = [
  { label: 'Secrets & API keys', result: '0 found', ok: true },
  { label: 'Dependencies', result: '1 high · fixed', ok: true },
  { label: 'Unprotected routes', result: '0 found', ok: true },
];

export const ScanPlate: React.FC = () => (
  <div className="psf psf-scan" aria-hidden="true">
    <div className="psf-scan__head">
      <span className="psf-scan__title"><i className="fa-solid fa-shield-halved" /> Security scan · task-13</span>
      <span className="psf-scan__badge">PASSED</span>
    </div>
    <div className="psf-scan__rows">
      {SCAN_CHECKS.map((check, i) => (
        <span key={check.label} className="psf-scan__row" style={dv(i)}>
          <i className="fa-solid fa-circle-check psf-scan__ok" />
          <span className="psf-scan__label">{check.label}</span>
          <span className="psf-scan__result">{check.result}</span>
        </span>
      ))}
    </div>
    <span className="psf-scan__bar"><span className="psf-scan__fill" /></span>
    <span className="psf-scan__foot">Cleared for staging · Deploy unlocked</span>
  </div>
);

const GANTT_BARS = [
  { l: 0, w: 36 },
  { l: 16, w: 30 },
  { l: 34, w: 40 },
  { l: 56, w: 34 },
];

export const PlanPlate: React.FC = () => (
  <div className="psf psf-plan" aria-hidden="true">
    <div className="psf-plan__arch">
      <span className="psf-plan__node">Web</span>
      <span className="psf-plan__wire" style={dv(0)} />
      <span className="psf-plan__node">API</span>
      <span className="psf-plan__wire" style={dv(1)} />
      <span className="psf-plan__node">DB</span>
    </div>
    <div className="psf-plan__gantt">
      {GANTT_BARS.map((b, i) => (
        <span
          key={`${b.l}-${b.w}`}
          className="psf-plan__bar"
          style={{ '--l': `${b.l}%`, '--w': `${b.w}%`, '--d': i } as React.CSSProperties}
        />
      ))}
    </div>
    <span className="psf-plan__btn">Start Building</span>
  </div>
);

const GIT_BRANCHES = [
  {
    d: 'M40 75 C52 75 52 35 64 35 H140 C152 35 152 75 164 75',
    label: 'task-12', lx: 66, ly: 27, dots: [[88, 35], [118, 35]], mx: 164,
  },
  {
    d: 'M70 75 C82 75 82 115 94 115 H196 C208 115 208 75 220 75',
    label: 'task-13', lx: 96, ly: 130, dots: [[124, 115], [162, 115]], mx: 220,
  },
  {
    d: 'M170 75 C182 75 182 35 194 35 H262 C274 35 274 75 286 75',
    label: 'task-14', lx: 196, ly: 27, dots: [[218, 35], [244, 35]], mx: 286,
  },
];

export const GitPlate: React.FC = () => (
  <div className="psf psf-git" aria-hidden="true">
    <svg viewBox="0 0 320 150" className="psf-git__svg">
      <path className="psf-git__trunk" d="M12 75 H308" pathLength={1} />
      <text className="psf-git__tlabel" x="14" y="92">develop</text>
      {GIT_BRANCHES.map((b, i) => (
        <g key={b.label} className="psf-git__branch" style={dv(i)}>
          <path d={b.d} pathLength={1} className="psf-git__line" />
          <text x={b.lx} y={b.ly} className="psf-git__blabel">{b.label}</text>
          {b.dots.map(([cx, cy]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3.5" className="psf-git__dot" />
          ))}
          <circle cx={b.mx} cy="75" r="4.5" className="psf-git__merge" />
        </g>
      ))}
    </svg>
    <span className="psf-git__legend">
      <i className="fa-solid fa-robot" /> 3 agents, 3 branches - merged by pull request
    </span>
  </div>
);

export const LivePlate: React.FC = () => (
  <div className="psf psf-live" aria-hidden="true">
    <div className="psf-browser">
      <div className="psf-browser__bar">
        <span className="psf-browser__dots" />
        <span className="psf-browser__addr">
          <i className="fa-solid fa-lock" />
          <span className="psf-live__type">gymcrm.preview.8080.ai</span>
          <span className="psf-live__caret" />
        </span>
      </div>
      <div className="psf-browser__view">
        <span className="psf-live__badge"><span className="psf-live__pulse" /> LIVE</span>
        <span className="psf-wire psf-wire--nav" style={dv(0)} />
        <div className="psf-wire__row">
          <span className="psf-wire psf-wire--card" style={dv(1)} />
          <span className="psf-wire psf-wire--card" style={dv(2)} />
        </div>
        <span className="psf-wire psf-wire--cta" style={dv(3)} />
      </div>
    </div>
    <div className="psf-live__meta">
      <span><i className="fa-solid fa-dharmachakra" /> your own namespace</span>
      <span><i className="fa-solid fa-rocket" /> Deploy on demand</span>
    </div>
  </div>
);
